import React, { useEffect, useState } from "react";
import { MapPin, Plus, Trash2, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import StructuredAddressForm from "./StructuredAddressForm";

/**
 * Reusable address book for the account page.
 *
 *   - Lists up to MAX (server-enforced 3) saved addresses
 *   - "Add new address" form expands inline
 *   - Each saved row has a Delete button
 *
 * No props — owns its own fetch + local state.
 */
const SavedAddressesSection = () => {
    const [saved, setSaved] = useState(null); // null = loading
    const [max, setMax] = useState(3);
    const [addOpen, setAddOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({
        label: "",
        country: "Albania",
        state: "",
        municipality: "",
        locality: "",
        postal_code: "",
        address: "",
        address_lat: null,
        address_lng: null,
    });

    const fetchList = async () => {
        try {
            const r = await api.get("/user/saved-addresses");
            setSaved(r.data.saved_addresses || []);
            setMax(r.data.max || 3);
        } catch (err) {
            setSaved([]);
        }
    };

    useEffect(() => { fetchList(); }, []);

    const canAddMore = saved && saved.length < max;

    const resetDraft = () => {
        setDraft({
            label: "", country: "Albania", state: "", municipality: "",
            locality: "", postal_code: "", address: "",
            address_lat: null, address_lng: null,
        });
    };

    const addAddress = async (e) => {
        e.preventDefault();
        if (!draft.label.trim() || !draft.municipality.trim() || !draft.address.trim()) {
            toast.error("Please fill in label, city and street address.");
            return;
        }
        const combined = [
            draft.address, draft.locality, draft.municipality,
            draft.postal_code, draft.state, draft.country || "Albania",
        ].filter(Boolean).join(", ");
        setAdding(true);
        try {
            await api.post("/user/saved-addresses", { ...draft, address: combined });
            toast.success("Address saved");
            resetDraft();
            setAddOpen(false);
            fetchList();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save address");
        } finally {
            setAdding(false);
        }
    };

    const deleteAddress = async (id) => {
        try {
            await api.delete(`/user/saved-addresses/${id}`);
            toast.success("Removed");
            fetchList();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not remove");
        }
    };

    return (
        <section
            data-testid="saved-addresses-section"
            className="mt-7 bg-white border border-neutral-200 rounded-2xl p-6"
        >
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#FF5722]" />
                    Saved addresses
                    <span className="text-xs font-normal text-neutral-500 ml-1">
                        {saved ? `(${saved.length}/${max})` : ""}
                    </span>
                </h2>
                {canAddMore && !addOpen && (
                    <button
                        type="button"
                        onClick={() => setAddOpen(true)}
                        data-testid="saved-addresses-add-btn"
                        className="h-9 px-3 rounded-full bg-[#FF5722] hover:bg-[#E64A19] text-white text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                    >
                        <Plus className="h-4 w-4" /> Add new
                    </button>
                )}
            </div>

            {saved === null ? (
                <div className="space-y-2">
                    {[1, 2].map((i) => (
                        <div key={i} className="h-16 bg-neutral-50 rounded-xl animate-pulse" />
                    ))}
                </div>
            ) : saved.length === 0 && !addOpen ? (
                <p
                    data-testid="saved-addresses-empty"
                    className="text-sm text-neutral-500"
                >
                    No saved addresses yet. Tap <span className="font-semibold text-neutral-700">Add new</span> to save your home, office, or any frequent delivery address.
                </p>
            ) : (
                <ul className="space-y-2.5">
                    {saved.map((a) => (
                        <li
                            key={a.id}
                            data-testid={`saved-address-row-${a.id}`}
                            className="border border-neutral-200 rounded-xl p-3.5 flex items-start gap-3"
                        >
                            <span className="h-9 w-9 rounded-full bg-[#FFF3EE] text-[#FF5722] inline-flex items-center justify-center shrink-0">
                                <MapPin className="h-4 w-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-neutral-900 truncate">
                                    {a.label}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5 truncate">
                                    {a.address}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => deleteAddress(a.id)}
                                data-testid={`saved-address-delete-${a.id}`}
                                aria-label="Remove address"
                                className="h-9 w-9 rounded-full text-neutral-400 hover:bg-rose-50 hover:text-rose-600 transition-colors flex items-center justify-center"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {!canAddMore && saved && saved.length >= max && !addOpen && (
                <p className="mt-3 text-[11px] text-neutral-500">
                    You&apos;ve reached the limit of {max} saved addresses. Delete one to add a new address.
                </p>
            )}

            {addOpen && (
                <form
                    data-testid="saved-addresses-add-form"
                    onSubmit={addAddress}
                    className="mt-4 pt-4 border-t border-neutral-100"
                >
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                        Nickname (e.g., Home, Office) *
                    </label>
                    <input
                        required
                        type="text"
                        maxLength={40}
                        value={draft.label}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        data-testid="saved-address-label-input"
                        placeholder="Home"
                        className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm mb-3"
                    />
                    <StructuredAddressForm
                        value={draft}
                        onChange={setDraft}
                        testIdPrefix="saved-address-"
                    />
                    <div className="mt-4 flex items-center gap-2">
                        <button
                            type="submit"
                            disabled={adding}
                            data-testid="saved-address-submit-btn"
                            className="h-10 px-4 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                        >
                            {adding ? "Saving…" : <><Check className="h-4 w-4" /> Save address</>}
                        </button>
                        <button
                            type="button"
                            onClick={() => { resetDraft(); setAddOpen(false); }}
                            data-testid="saved-address-cancel-btn"
                            className="h-10 px-4 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors"
                        >
                            <X className="h-4 w-4" /> Cancel
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
};

export default SavedAddressesSection;
